import { GQLProductFragment, GQLProductSideSideType } from '@theprintbar/graphql';
import { Merch, cloneDeep, getEmptyTeamNames, invariant } from '@theprintbar/shared';
import type { CreativeEditorInstance } from '../../CreativeEditor';
import { editorTools } from '../../CreativeEditor/editorTools';

interface EditorUpdateProps {
	editor: CreativeEditorInstance;
	product: GQLProductFragment;
	currentMerch: Merch;
	activeSide?: GQLProductSideSideType;
	domain: string;
	dpi?: number;
	firstUpdate?: boolean;
}

export async function handleEditorUpdate({
	editor,
	product,
	domain,
	currentMerch,
	activeSide,
	dpi,
	firstUpdate,
}: EditorUpdateProps): Promise<Merch> {
	const newMerch = cloneDeep(currentMerch) as Merch;

	if (!editor) return newMerch;

	invariant(!activeSide || product.sides.some(side => side.side === activeSide), 'Active side must be one of the product sides');

	for (const side of product.sides) {
		if (activeSide && side.side !== activeSide) continue;

		const decorationIndex = newMerch.decorations.findIndex(decoration => decoration.side === side.side);

		if (decorationIndex === -1) throw 'Decoration not found';

		const decoration = newMerch.decorations[decorationIndex];
		const decorationType = decoration?.type || side.availableDecorations[0];

		const artworkAndMeta = await editorTools.getArtworkFromPage({
			editor,
			decoration,
			dpi,
			domain,
			colourHex: product.customDecorationFixedColour,
		});
		//Update the teamnames in case one was deleted
		const availableTeamNames = editorTools.getCurrentTeamNameVariables(editor);
		newMerch.teamNames =
			availableTeamNames.length === 0
				? undefined
				: {
						//Use existing teamNameId if it exists otherwise generate a new one
						teamNameId: newMerch.teamNames?.teamNameId || crypto.getRandomValues(new Uint32Array(1))[0].toString(),
						available: availableTeamNames,
						selected: newMerch.teamNames?.selected || getEmptyTeamNames(availableTeamNames, newMerch),
				  };

		if (decorationIndex > -1) {
			newMerch.decorations[decorationIndex] = {
				...decoration,
				...artworkAndMeta,
				meta: {
					...artworkAndMeta.meta,
					requestedColours: decoration.meta.requestedColours,
					stitches: firstUpdate ? decoration.meta.stitches : 0,
					useSuppliedEmbFile: decoration.meta.useSuppliedEmbFile,
				},
			};
		} else {
			newMerch.decorations.push({
				...artworkAndMeta,
				side: side.side,
				type: decorationType,
			});
		}

		newMerch.scene = await editorTools.getSceneString(editor);
	}

	return newMerch;
}
